/**
 * Canvas 2D board renderer — the desktop's jungle/gui/assets.py artwork
 * restyled as "refined classic": a wooden frame with bevels, parchment cells
 * with a soft vignette, and glossy gradient piece discs with drop shadows.
 * The palette keeps the desktop's color identity.
 *
 * Rendering is layered for performance: the static background (terrain, grid,
 * frame, vignette) is painted into an offscreen canvas that is repainted only
 * on resize or flip; each frame then costs one drawImage plus the pieces and
 * overlays. Piece gradients are cached per (side, cell size).
 */

import type { MoveRef, Pos } from '../protocol/messages';
import { cellSide } from '../protocol/messages';
import {
  COLS,
  ROWS,
  cellRect,
  frameGeometry,
  gridGeometry,
  type GridGeometry,
  type Rect,
} from './coordinates';

// -- palette (base values from jungle/gui/assets.py; gloss/frame added) -------

export const PALETTE = {
  LAND_LIGHT: '#EBDDB4',
  LAND_DARK: '#DCC893',
  RIVER_FILL: '#3D7AB8',
  RIVER_RIPPLE: '#8FC3EE',
  TRAP_FILL: '#E3B54C',
  TRAP_MARKER: '#7A5B12',
  DEN_FILL_RED: '#7E2A1E',
  DEN_FILL_BLUE: '#1F4E79',
  DEN_STAR: '#F4D03F',
  GRID_LINE: '#6E5B34',
  SELECTION_RING: '#F8C471',
  LEGAL_DOT: 'rgba(39, 174, 96, 0.75)',
  LEGAL_CAPTURE: 'rgba(231, 76, 60, 0.86)',
  LAST_MOVE: '#E67E22',
  CAPTURE_FLASH: '#C0392B',
  PIECE_FILL: { RED: '#C0392B', BLUE: '#2471A3' },
  PIECE_RIM: { RED: '#6E1B12', BLUE: '#123F5E' },
  PIECE_GLOSS: { RED: '#DC928A', BLUE: '#87B1CC' },
  PIECE_TEXT: '#FDFEFE',
  PIECE_TEXT_OUTLINE: 'rgba(0, 0, 0, 0.67)',
  PIECE_SHADOW: 'rgba(20, 10, 0, 0.35)',
  FRAME_LIGHT: '#A9713B',
  FRAME_MID: '#7C4F24',
  FRAME_DARK: '#54340F',
  FRAME_BEVEL_IN: 'rgba(255, 232, 190, 0.40)',
  FRAME_BEVEL_OUT: 'rgba(0, 0, 0, 0.50)',
  BOARD_SHADOW: 'rgba(0, 0, 0, 0.45)',
  VIGNETTE: 'rgba(24, 16, 6, 0.12)',
  HOVER_INNER: 'rgba(255, 255, 255, 0.20)',
  HOVER_OUTER: 'rgba(255, 255, 255, 0.02)',
} as const;

export const RANK_EMOJI: readonly (string | null)[] = [
  null, '🐭', '🐱', '🐶', '🐺', '🐆', '🐯', '🦁', '🐘',
];

export const RANK_LABEL: readonly (string | null)[] = [
  null, 'RAT', 'CAT', 'DOG', 'WLF', 'LEO', 'TIG', 'LIO', 'ELE',
];

const EMOJI_FONT = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
const LABEL_FONT = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

// -- terrain lookup (geometry only — mirrors jungle/model/constants.py) -------

type Terrain = 'land' | 'river' | 'trap_red' | 'trap_blue' | 'den_red' | 'den_blue';

const DEN: Record<string, Pos> = { BLUE: [0, 3], RED: [8, 3] };
const TRAPS: Record<string, ReadonlySet<string>> = {
  BLUE: new Set(['0,2', '0,4', '1,3']),
  RED: new Set(['8,2', '8,4', '7,3']),
};
const RIVER = new Set<string>();
for (const row of [3, 4, 5]) {
  for (const col of [1, 2, 4, 5]) RIVER.add(`${row},${col}`);
}

function terrainAt(row: number, col: number): Terrain {
  const key = `${row},${col}`;
  if (row === DEN.RED[0] && col === DEN.RED[1]) return 'den_red';
  if (row === DEN.BLUE[0] && col === DEN.BLUE[1]) return 'den_blue';
  if (TRAPS.RED.has(key)) return 'trap_red';
  if (TRAPS.BLUE.has(key)) return 'trap_blue';
  if (RIVER.has(key)) return 'river';
  return 'land';
}

// -- view state ------------------------------------------------------------------

export interface BoardView {
  cells: (number | null)[];
  flipped: boolean;
  selected: Pos | null;
  /** Legal destinations for the selected piece (with occupancy for dot-vs-ring). */
  targets: Pos[];
  lastMove: MoveRef | null;
  /** Square the mouse hovers over (own movable piece), or null. */
  hover: Pos | null;
  /** Piece currently animating: drawn at `center`; the static piece on the
   *  `skip` square (its destination in the new snapshot) is omitted. */
  animating: { cell: number; skip: Pos; center: { x: number; y: number }; scale?: number } | null;
}

export function cellIndex(row: number, col: number): number {
  return row * COLS + col;
}

// -- renderer ----------------------------------------------------------------------

export class BoardRenderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;
  private bgCtx: CanvasRenderingContext2D;
  /** Flip state the background was painted for (cache key alongside size). */
  private bgFlipped: boolean | null = null;
  private dpr = 1;
  private geom: GridGeometry = { cell: 0, originX: 0, originY: 0 };
  private cssWidth = 0;
  private cssHeight = 0;
  private discGrad = new Map<string, CanvasGradient>();
  private glossGrad: CanvasGradient | null = null;
  private glossCell = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    const bgCtx = this.bg.getContext('2d');
    if (!bgCtx) throw new Error('Canvas 2D is not available');
    this.bgCtx = bgCtx;
  }

  get geometry(): GridGeometry {
    return this.geom;
  }

  /** Resize both layers to CSS size × devicePixelRatio; clears all caches. */
  resize(cssWidth: number, cssHeight: number): void {
    this.cssWidth = cssWidth;
    this.cssHeight = cssHeight;
    this.dpr = window.devicePixelRatio || 1;
    for (const [target, context] of [
      [this.canvas, this.ctx],
      [this.bg, this.bgCtx],
    ] as const) {
      target.width = Math.max(1, Math.round(cssWidth * this.dpr));
      target.height = Math.max(1, Math.round(cssHeight * this.dpr));
      context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }
    this.geom = gridGeometry(cssWidth, cssHeight);
    this.discGrad.clear();
    this.glossGrad = null;
    this.bgFlipped = null; // force background repaint
  }

  render(view: BoardView): void {
    // Zoom/monitor-drag changes dpr without firing either resize source.
    const dpr = window.devicePixelRatio || 1;
    if (dpr !== this.dpr) this.resize(this.cssWidth, this.cssHeight);

    if (view.flipped !== this.bgFlipped) this.paintBackground(view.flipped);
    const { ctx } = this;
    ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);
    ctx.drawImage(this.bg, 0, 0, this.cssWidth, this.cssHeight);
    if (view.lastMove) this.paintLastMove(view.lastMove, view.flipped);
    this.paintTargets(view);
    this.paintPieces(view);
    if (view.hover) this.paintHover(view.hover, view.flipped);
  }

  // -- background layer (static: terrain + grid + frame + vignette) -------------

  private paintBackground(flipped: boolean): void {
    const g = this.bgCtx;
    g.clearRect(0, 0, this.cssWidth, this.cssHeight);
    this.paintCells(g, flipped);
    this.paintGrid(g);
    this.paintFrame(g);
    this.paintVignette(g);
    this.bgFlipped = flipped;
  }

  private paintCells(g: CanvasRenderingContext2D, flipped: boolean): void {
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const rect = cellRect([row, col], flipped, this.geom);
        switch (terrainAt(row, col)) {
          case 'river':
            this.fill(g, rect, PALETTE.RIVER_FILL);
            this.drawRipples(g, rect);
            break;
          case 'trap_red':
          case 'trap_blue':
            this.fill(g, rect, this.landColor(row, col));
            this.fill(g, rect, PALETTE.TRAP_FILL);
            this.drawTrapMarker(g, rect);
            break;
          case 'den_red':
            this.fill(g, rect, PALETTE.DEN_FILL_RED);
            this.drawDenStar(g, rect);
            break;
          case 'den_blue':
            this.fill(g, rect, PALETTE.DEN_FILL_BLUE);
            this.drawDenStar(g, rect);
            break;
          default:
            this.fill(g, rect, this.landColor(row, col));
        }
      }
    }
  }

  private landColor(row: number, col: number): string {
    return (row + col) % 2 === 0 ? PALETTE.LAND_LIGHT : PALETTE.LAND_DARK;
  }

  private paintGrid(g: CanvasRenderingContext2D): void {
    const { geom } = this;
    g.strokeStyle = PALETTE.GRID_LINE;
    g.lineWidth = 1.5;
    g.beginPath();
    for (let col = 0; col <= COLS; col++) {
      const x = Math.round(geom.originX + col * geom.cell) + 0.5;
      g.moveTo(x, geom.originY);
      g.lineTo(x, geom.originY + ROWS * geom.cell);
    }
    for (let row = 0; row <= ROWS; row++) {
      const y = Math.round(geom.originY + row * geom.cell) + 0.5;
      g.moveTo(geom.originX, y);
      g.lineTo(geom.originX + COLS * geom.cell, y);
    }
    g.stroke();
  }

  /** Wooden frame ring with bevels and a board-level drop shadow. */
  private paintFrame(g: CanvasRenderingContext2D): void {
    const f = frameGeometry(this.geom);
    const innerRadius = Math.max(2, f.radius - f.t);
    const outer = new Path2D();
    outer.roundRect(f.x, f.y, f.w, f.h, f.radius);
    const ring = new Path2D();
    ring.roundRect(f.x, f.y, f.w, f.h, f.radius);
    ring.roundRect(f.x + f.t, f.y + f.t, f.w - 2 * f.t, f.h - 2 * f.t, innerRadius);

    const wood = g.createLinearGradient(f.x, f.y, f.x + f.w, f.y + f.h);
    wood.addColorStop(0, PALETTE.FRAME_LIGHT);
    wood.addColorStop(0.5, PALETTE.FRAME_MID);
    wood.addColorStop(1, PALETTE.FRAME_DARK);

    // The shadow hugs the frame (outer edge casts onto the page, inner edge
    // casts a subtle inset shadow onto the edge cells). Blur is not scaled
    // by the transform, so multiply by dpr explicitly.
    g.save();
    g.shadowColor = PALETTE.BOARD_SHADOW;
    g.shadowBlur = 16 * this.dpr;
    g.shadowOffsetY = 5 * this.dpr;
    g.fillStyle = wood;
    g.fill(ring, 'evenodd');
    g.restore();

    // Bevel edges (strokes ignore the fill rule, so use standalone paths).
    g.strokeStyle = PALETTE.FRAME_BEVEL_OUT;
    g.lineWidth = 1.5;
    g.stroke(outer);
    const inner = new Path2D();
    inner.roundRect(f.x + f.t, f.y + f.t, f.w - 2 * f.t, f.h - 2 * f.t, innerRadius);
    g.strokeStyle = PALETTE.FRAME_BEVEL_IN;
    g.lineWidth = 1.5;
    g.stroke(inner);
  }

  /** Soft darkening toward the board's corners, clipped to the frame. */
  private paintVignette(g: CanvasRenderingContext2D): void {
    const f = frameGeometry(this.geom);
    const cx = f.x + f.w / 2;
    const cy = f.y + f.h / 2;
    const radius = Math.max(f.w, f.h) * 0.72;
    const vignette = g.createRadialGradient(cx, cy, radius * 0.4, cx, cy, radius);
    vignette.addColorStop(0, 'rgba(24, 16, 6, 0)');
    vignette.addColorStop(1, PALETTE.VIGNETTE);
    const clip = new Path2D();
    clip.roundRect(f.x, f.y, f.w, f.h, f.radius);
    g.save();
    g.clip(clip);
    g.fillStyle = vignette;
    g.fillRect(f.x, f.y, f.w, f.h);
    g.restore();
  }

  // -- per-frame overlays ---------------------------------------------------------

  private paintLastMove(lastMove: MoveRef, flipped: boolean): void {
    const { ctx } = this;
    ctx.strokeStyle = lastMove.capture ? PALETTE.CAPTURE_FLASH : PALETTE.LAST_MOVE;
    ctx.lineWidth = 3;
    for (const pos of [lastMove.from, lastMove.to]) {
      const rect = cellRect(pos, flipped, this.geom);
      ctx.strokeRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h - 4);
    }
  }

  private paintTargets(view: BoardView): void {
    const { ctx } = this;
    for (const pos of view.targets) {
      const rect = cellRect(pos, view.flipped, this.geom);
      const occupant = view.cells[cellIndex(pos[0], pos[1])];
      if (occupant !== null && occupant !== undefined) {
        // Capture target: red ring.
        ctx.strokeStyle = PALETTE.LEGAL_CAPTURE;
        ctx.lineWidth = Math.max(3, rect.w * 0.05);
        ctx.beginPath();
        ctx.ellipse(rect.x + rect.w / 2, rect.y + rect.h / 2,
          rect.w / 2 - 5, rect.h / 2 - 5, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.fillStyle = PALETTE.LEGAL_DOT;
        ctx.beginPath();
        ctx.arc(rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w * 0.14, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private paintHover(pos: Pos, flipped: boolean): void {
    const { ctx } = this;
    const rect = cellRect(pos, flipped, this.geom);
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    const radius = rect.w * 0.43;
    const glow = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius);
    glow.addColorStop(0, PALETTE.HOVER_INNER);
    glow.addColorStop(1, PALETTE.HOVER_OUTER);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private paintPieces(view: BoardView): void {
    const skip = view.animating?.skip;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const cell = view.cells[cellIndex(row, col)];
        if (cell === null || cell === undefined) continue;
        // The animating piece is drawn separately at its interpolated center.
        if (skip && skip[0] === row && skip[1] === col) continue;
        const rect = cellRect([row, col], view.flipped, this.geom);
        const selected = view.selected !== null
          && view.selected[0] === row && view.selected[1] === col;
        this.drawPiece(rect, cell, selected);
      }
    }
    if (view.animating) {
      const { cell, center, scale } = view.animating;
      const size = this.geom.cell;
      this.drawPiece({ x: center.x - size / 2, y: center.y - size / 2, w: size, h: size },
        cell, false, scale ?? 1);
    }
  }

  // -- pieces -----------------------------------------------------------------------

  /** One animal piece: glossy gradient disc + emoji + outlined short label. */
  drawPiece(rect: Rect, cell: number, selected: boolean, scale = 1): void {
    const { ctx } = this;
    const rank = cell & 15;
    const side = cellSide(cell);
    const margin = rect.w * 0.07;
    const radius = (rect.w - 2 * margin) / 2;
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;

    ctx.save();
    ctx.translate(cx, cy);
    if (scale !== 1) ctx.scale(scale, scale);

    // Disc fill with drop shadow (shadow confined to this save block so the
    // emoji and label stay crisp).
    ctx.save();
    ctx.shadowColor = PALETTE.PIECE_SHADOW;
    ctx.shadowBlur = Math.max(3, rect.w * 0.06) * this.dpr;
    ctx.shadowOffsetY = Math.max(1.5, rect.w * 0.03);
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fillStyle = this.discGradient(side, rect.w, radius);
    ctx.fill();
    ctx.restore();

    // Rim.
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.strokeStyle = PALETTE.PIECE_RIM[side];
    ctx.lineWidth = Math.max(2, rect.w * 0.03);
    ctx.stroke();

    // Gloss highlight, clipped to the disc.
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = this.glossGradient(rect.w, radius);
    ctx.fillRect(-radius, -radius, 2 * radius, 2 * radius);
    ctx.restore();

    // Animal emoji, centered in the upper ~2/3 of the disc.
    const emoji = RANK_EMOJI[rank];
    if (emoji) {
      ctx.font = `${Math.floor(rect.h * 0.42)}px ${EMOJI_FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(emoji, 0, -radius * 0.28);
    }

    // Short name with a dark outline so it reads on any disc color.
    const label = RANK_LABEL[rank];
    if (label) {
      const px = Math.max(9, Math.floor(rect.h * 0.19));
      ctx.font = `bold ${px}px ${LABEL_FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = Math.max(2, rect.w * 0.025);
      ctx.strokeStyle = PALETTE.PIECE_TEXT_OUTLINE;
      ctx.strokeText(label, 0, radius * 0.60);
      ctx.fillStyle = PALETTE.PIECE_TEXT;
      ctx.fillText(label, 0, radius * 0.60);
    }
    ctx.restore();

    if (selected) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 3, 0, Math.PI * 2);
      ctx.strokeStyle = PALETTE.SELECTION_RING;
      ctx.lineWidth = Math.max(3, rect.w * 0.05);
      ctx.stroke();
    }
  }

  /** Cached radial disc gradient in local disc coordinates (translate-first). */
  private discGradient(side: 'RED' | 'BLUE', cellPx: number, radius: number): CanvasGradient {
    const key = `${side}:${Math.round(cellPx)}`;
    let grad = this.discGrad.get(key);
    if (!grad) {
      grad = this.ctx.createRadialGradient(
        -radius * 0.30, -radius * 0.36, radius * 0.08, 0, 0, radius * 1.05);
      grad.addColorStop(0, PALETTE.PIECE_GLOSS[side]);
      grad.addColorStop(0.55, PALETTE.PIECE_FILL[side]);
      grad.addColorStop(1, PALETTE.PIECE_RIM[side]);
      this.discGrad.set(key, grad);
    }
    return grad;
  }

  /** Cached gloss highlight gradient, one per cell size. */
  private glossGradient(cellPx: number, radius: number): CanvasGradient {
    const key = Math.round(cellPx);
    if (this.glossGrad === null || this.glossCell !== key) {
      const grad = this.ctx.createRadialGradient(
        -radius * 0.28, -radius * 0.34, 0, -radius * 0.28, -radius * 0.34, radius * 0.75);
      grad.addColorStop(0, 'rgba(255, 255, 255, 0.50)');
      grad.addColorStop(0.35, 'rgba(255, 255, 255, 0.16)');
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      this.glossGrad = grad;
      this.glossCell = key;
    }
    return this.glossGrad;
  }

  // -- glyph helpers (background layer) --------------------------------------------

  private fill(g: CanvasRenderingContext2D, rect: Rect, color: string): void {
    g.fillStyle = color;
    g.fillRect(rect.x, rect.y, rect.w, rect.h);
  }

  /** Two wave strokes across a river cell. */
  private drawRipples(g: CanvasRenderingContext2D, rect: Rect): void {
    g.strokeStyle = PALETTE.RIVER_RIPPLE;
    g.lineWidth = Math.max(1.5, rect.w * 0.03);
    for (const frac of [0.35, 0.65]) {
      const y = rect.y + rect.h * frac;
      g.beginPath();
      g.moveTo(rect.x + rect.w * 0.15, y);
      g.quadraticCurveTo(rect.x + rect.w / 2, y - rect.h * 0.18,
        rect.x + rect.w * 0.85, y);
      g.stroke();
    }
  }

  /** Warning-triangle glyph centered in a trap cell. */
  private drawTrapMarker(g: CanvasRenderingContext2D, rect: Rect): void {
    const cx = rect.x + rect.w / 2;
    g.beginPath();
    g.moveTo(cx, rect.y + rect.h * 0.22);
    g.lineTo(rect.x + rect.w * 0.22, rect.y + rect.h * 0.74);
    g.lineTo(rect.x + rect.w * 0.78, rect.y + rect.h * 0.74);
    g.closePath();
    g.strokeStyle = PALETTE.TRAP_MARKER;
    g.lineWidth = Math.max(2, rect.w * 0.035);
    g.stroke();
  }

  /** Five-pointed star centered in a den cell. */
  private drawDenStar(g: CanvasRenderingContext2D, rect: Rect): void {
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    const outer = rect.w * 0.30;
    const inner = outer * 0.45;
    g.beginPath();
    for (let i = 0; i < 10; i++) {
      const radius = i % 2 === 0 ? outer : inner;
      const angle = -Math.PI / 2 + (i * Math.PI) / 5;
      const px = cx + radius * Math.cos(angle);
      const py = cy + radius * Math.sin(angle);
      if (i === 0) g.moveTo(px, py);
      else g.lineTo(px, py);
    }
    g.closePath();
    g.fillStyle = PALETTE.DEN_STAR;
    g.fill();
    g.strokeStyle = PALETTE.DEN_STAR;
    g.lineWidth = Math.max(1.5, rect.w * 0.02);
    g.stroke();
  }
}
